import Info from "@eisinc/XDL/Icons/Info";

import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    pageType: getCurrentPage(state).type,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    showCartUrl?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    wrappingContainer?: InjectableCss;
    summaryOrdercontainer?: InjectableCss;
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    cartUrl?: LinkPresentationProps;
    labelGridItem?: InjectableCss;
    valueGridItem?: InjectableCss;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    freeShippingStyle?: TypographyProps;
    freeShippingWrapperStyle?: InjectableCss;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    wrappingContainer: {
        css: css`
            width: 100%;
        `,
    },
    summaryOrdercontainer: {
        css: css`
            border-bottom: 1px solid ${getColor("text.main")};
            padding-bottom: 15px;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-top: 15px;
            width: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            border-bottom: 1px solid ${getColor("text.main")};
            padding-bottom: 15px;

            & h3 {
                font-size: 26px;
                line-height: 32px;
                font-weight: bold;
                @media print {
                    font-size: 16px;
                    font-weight: bold;
                    line-height: 22px;
                }
            }

            & span {
                display: block;
                font-size: 14px;
                line-height: 17px;
            }
        `,
    },
    cartUrl: {
        typographyProps: {
            size: "14px",
            lineHeight: "17px",
            css: css`
                margin-left: 8px;
            `,
        },
    },
    labelGridItem: {
        css: css`
            display: flex;
        `,
    },
    valueGridItem: {},
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: { weight: "bold" },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        color: "primary.main",
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    freeShippingStyle: {
        css: css`
            font-size: 12px;
            font-style: italic;
            padding: 0 5px;
        `,
    },
    freeShippingWrapperStyle: {
        css: css`
            font-size: 12px;
            font-style: italic;
            margin-top: -5px;
        `,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    extendedStyles,
    cartPageUrl,
    showCartUrl,
    pageType,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));
    const isQuotePage = cart?.type === "Quote" || cart?.status === "TempQuote";
    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (isLoading || !cart) {
        return (
            <StyledWrapper {...styles.wrappingContainer}>
                <StyledWrapper {...styles.summaryOrdercontainer}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography as="h3">{translate("Order Summary")}</Typography>
                        <StyledWrapper
                            css={css`
                                display: inline-flex;
                                line-height: 17px;
                            `}
                        >
                            <Typography as="span">
                                {cart?.cartLines?.length} {translate("Items")}
                            </Typography>
                            {showCartUrl && cart && !isQuotePage && (
                                <Link {...styles.cartUrl} href={cartPageUrl}>
                                    {translate("Edit Shopping Cart")}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {!showTaxAndShipping && (
                        <StyledWrapper {...styles.wrapper}>
                            <StyledWrapper {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.valueGridItem}>
                                <LoadingSpinner {...styles.loadingSpinner} />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                {showTaxAndShipping && (
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    if (isCartEmpty) {
        return null;
    }

    const splitShippingAndHandling = true;
    // cart.properties?.shipping && cart.properties?.handling;
    // changed manually to true bcz we always should depend on the custom props
    const isTBD = pageType === "CheckoutShippingPage" ? translate("TBD") : false;
    const isShippingHandlingTBD = isTBD;
    const isShippingTBD = isTBD;
    const isHandlingTBD = isTBD;
    const isTaxTBD = isTBD;
    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);
    return (
        <StyledWrapper {...styles.wrappingContainer}>
            <StyledWrapper {...styles.summaryOrdercontainer} data-test-selector={`cartTotal_${cart.id}`}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h3">{translate("Order Summary")}</Typography>
                    <StyledWrapper
                        css={css`
                            display: inline-flex;
                            line-height: 17px;
                        `}
                    >
                        <Typography as="span">
                            {cart?.cartLines?.length} {translate("Items")}
                        </Typography>
                        {showCartUrl && !isQuotePage && (
                            <Link {...styles.cartUrl} href={cartPageUrl}>
                                {translate("Edit Shopping Cart")}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {cart.orderSubTotalDisplay}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
                {filteredOrderPromotions &&
                    filteredOrderPromotions.map(promotion => (
                        <StyledWrapper key={promotion.id} {...styles.wrapper}>
                            <StyledWrapper {...styles.labelGridItem}>
                                <Typography
                                    {...styles.promotionLabel}
                                    data-test-selector="cartTotal_orderPromotionName"
                                >
                                    {`${translate("Promotion")}: ${promotion.name}`}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.valueGridItem}>
                                <Typography
                                    {...styles.promotionValue}
                                    data-test-selector="cartTotal_orderPromotionValue"
                                >
                                    {`-${promotion.amountDisplay}`}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                {/* {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && !splitShippingAndHandling && (
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.labelGridItem}>
                            <Typography {...styles.shippingAndHandlingLabel}>
                                {translate("Shipping & Handling")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.valueGridItem}>
                            <Typography
                                {...styles.shippingAndHandlingValue}
                                data-test-selector="cartTotal_shippingAndHandlingAmount"
                            >
                                {isShippingHandlingTBD || cart.shippingAndHandlingDisplay}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )} */}
                {showTaxAndShipping && (
                    <>
                        {cart.properties?.shipping && (
                            <StyledWrapper {...styles.wrapper}>
                                <StyledWrapper {...styles.labelGridItem}>
                                    <Typography {...styles.shippingAndHandlingLabel}>
                                        {translate("Shipping")}
                                    </Typography>
                                    <StyledWrapper
                                        className="popup-content-wrapper"
                                        css={css`
                                            margin-left: 5px;
                                            @media print {
                                                display: none;
                                            }
                                        `}>
                                        <Tooltip
                                            text={siteMessage("Custom_OrderSummary_Shipping_AdditionalInformation") as string}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.shippingAndHandlingValue}
                                        data-test-selector="cartTotalShippingAndHandlingAmount"
                                    >
                                        {isShippingTBD || cart.properties?.shipping}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        {pageType === "CheckoutReviewAndSubmitPage" &&
                            cart.properties?.shipping &&
                            cart.properties?.displayShippingPromotionMessageOnCart !== "undefined" &&
                            cart.properties?.displayShippingPromotionMessageOnCart?.toLowerCase() === "true" && (
                                <StyledWrapper {...styles.freeShippingWrapperStyle}>
                                    <Typography {...styles.freeShippingStyle}>
                                        {siteMessage("Custom_free_shipping", "Free Shipping")}
                                    </Typography>
                                </StyledWrapper>
                            )}
                        {cart.properties?.handling && (
                            <StyledWrapper {...styles.wrapper}>
                                <StyledWrapper {...styles.labelGridItem}>
                                    <Typography {...styles.shippingAndHandlingLabel}>
                                        {translate("Handling")}
                                    </Typography>
                                    <StyledWrapper
                                        className="popup-content-wrapper"
                                        css={css`
                                            margin-left: 5px;
                                            @media print {
                                                display: none;
                                            }
                                        `}>
                                        <Tooltip
                                            text={siteMessage("Custom_OrderSummary_Handling_AdditionalInformation") as string}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.shippingAndHandlingValue}
                                        data-test-selector="cartTotalShippingAndHandlingAmount"
                                    >
                                        {isHandlingTBD || cart.properties?.handling}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </>
                )}
                {filteredShippingPromotions  &&
                    filteredShippingPromotions .map(promotion => (
                        <StyledWrapper key={promotion.id} {...styles.wrapper}>
                            <StyledWrapper {...styles.labelGridItem}>
                                <Typography {...styles.promotionLabel}>
                                    {`${translate("Promotion")}: ${promotion.name}`}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.valueGridItem}>
                                <Typography
                                    {...styles.promotionValue}
                                    data-test-selector="cartTotal_shippingPromotionValue"
                                >
                                    {`-${promotion.amountDisplay}`}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT") : translate("Tax")}
                            </Typography>
                            <StyledWrapper>
                                <Tooltip
                                    text={siteMessage("Custom_OrderSummary_Tax_AdditionalInformation") as string}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                {isTaxTBD || cart.totalTaxDisplay}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showTaxAndShipping &&
                    [...cart.customerOrderTaxes!]
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(tax => {
                            const key = `${tax.taxCode}_${tax.sortOrder}`;
                            return (
                                <StyledWrapper key={key} {...styles.wrapper}>
                                    <StyledWrapper {...styles.labelGridItem}>
                                        <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.valueGridItem}>
                                        <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                {discountTotal !== undefined && discountTotal > 0 && (
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.labelGridItem}>
                            <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.valueGridItem}>
                            <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                                {`${translate("You saved")} `}{" "}
                                <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {showTaxAndShipping && (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.labelGridItem}>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.valueGridItem}>
                        <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                            {cart.orderGrandTotalDisplay}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(CartTotalDisplay);
